import { useEffect, useState } from "react";
import { Trophy, Users, Pencil, Trash2, RotateCcw, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { title: "", description: "", target: 3, points: 30 };

function ChallengeForm({ initial, category, week, templates = [], onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const applyTemplate = (t) => setF((s) => ({ ...s, ...t }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/clinician/challenges", { category, week, title: f.title, description: f.description, target: Number(f.target), points: Number(f.points) });
      toast.success(`Challenge set for ${category} · ${week === "this" ? "this week" : "next week"}.`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-3 space-y-2 rounded-2xl bg-stone-50 border border-stone-200 p-3" data-testid={`challenge-form-${category}-${week}`}>
      {templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="challenge-templates">
          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 inline-flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Start from an idea</span>
          {templates.map((t, i) => (
            <button type="button" key={t.title} onClick={() => applyTemplate(t)} data-testid={`challenge-template-${i}`} title={t.description}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${f.title === t.title ? "bg-amber-700 text-white border-amber-700" : "bg-white border-stone-300 text-stone-700 hover:border-amber-600"}`}>
              {t.title} <span className="font-mono opacity-70">· {t.target} · +{t.points}</span>
            </button>
          ))}
        </div>
      )}
      <input value={f.title} onChange={set("title")} placeholder="Title — e.g. Five sunrise walks" maxLength={80} required data-testid="challenge-title"
        className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm bg-white focus:border-amber-600 outline-none" />
      <textarea value={f.description} onChange={set("description")} placeholder="Why this week? What counts? (optional)" maxLength={400} rows={2} data-testid="challenge-description"
        className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm bg-white focus:border-amber-600 outline-none" />
      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-xs text-stone-600">Target check-ins
          <input type="number" min={1} max={14} value={f.target} onChange={set("target")} data-testid="challenge-target" className="block w-24 mt-1 px-3 py-2 rounded-xl border border-stone-300 text-sm bg-white" />
        </label>
        <label className="text-xs text-stone-600">Reward points
          <input type="number" min={10} max={100} step={5} value={f.points} onChange={set("points")} data-testid="challenge-points" className="block w-24 mt-1 px-3 py-2 rounded-xl border border-stone-300 text-sm bg-white" />
        </label>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 rounded-full border border-stone-300 text-xs font-semibold text-stone-700" data-testid="challenge-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="amber-btn text-xs px-4" data-testid="challenge-save">{busy ? "Saving…" : "Save challenge"}</button>
        </div>
      </div>
    </form>
  );
}

function ChallengeRow({ ch, templates, onChanged }) {
  const [editing, setEditing] = useState(false);
  const remove = async () => {
    try {
      await api.delete(`/clinician/challenges/${ch.id}`);
      toast("Back to the default challenge for that week.");
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <li className={`rounded-2xl border p-3 ${ch.custom ? "bg-amber-50/60 border-amber-200" : "bg-white border-stone-200"}`} data-testid={`clin-challenge-${ch.category}-${ch.which}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[12rem]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 capitalize">{ch.category} {ch.custom && (ch.mine ? <span className="ml-1 text-amber-800">· yours</span> : <span className="ml-1 text-stone-500 normal-case" data-testid="clin-challenge-owner">· created by {ch.created_by_name}</span>)}</div>
          <div className="font-semibold text-stone-900 text-sm" data-testid="clin-challenge-title">{ch.title}</div>
          <div className="text-xs text-stone-600 mt-0.5">{ch.description}</div>
          <div className="inline-flex items-center gap-1 text-[11px] text-stone-500 mt-1"><Users className="w-3 h-3" /> {ch.participants} joined · {ch.finishers} finished · {ch.target} check-ins · +{ch.points} pts</div>
        </div>
        {(!ch.custom || ch.mine) ? (
        <div className="flex gap-1">
          <button onClick={() => setEditing((v) => !v)} className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-900 hover:text-white flex items-center justify-center" title={ch.custom ? "Edit" : "Write a custom challenge"} data-testid={`clin-challenge-edit-${ch.category}-${ch.which}`}><Pencil className="w-3.5 h-3.5" /></button>
          {ch.custom && <button onClick={remove} className="w-8 h-8 rounded-full bg-stone-100 hover:bg-red-600 hover:text-white flex items-center justify-center" title="Remove — use the default" data-testid={`clin-challenge-delete-${ch.category}-${ch.which}`}>{ch.which === "this" ? <RotateCcw className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}</button>}
        </div>
        ) : <span className="text-[10px] text-stone-400 self-center" data-testid={`clin-challenge-locked-${ch.category}-${ch.which}`}>Only its author can change it</span>}
      </div>
      {editing && <ChallengeForm initial={ch.custom ? ch : { ...EMPTY, target: ch.target, points: ch.points }} category={ch.category} week={ch.which} templates={templates} onSaved={() => { setEditing(false); onChanged(); }} onCancel={() => setEditing(false)} />}
    </li>
  );
}

export function ChallengeCreator() {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState({});
  const [which, setWhich] = useState("this");
  const load = () => api.get("/clinician/challenges").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/clinician/challenges/templates").then((r) => setTemplates(r.data.templates)).catch(() => {}); }, []);
  if (!data) return null;
  const rows = data.challenges.filter((c) => c.which === which);
  return (
    <section className="warm-card p-6" data-testid="challenge-creator">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800"><Trophy className="w-3.5 h-3.5" /> Community challenges</div>
          <h3 className="font-display text-xl font-bold text-stone-900 mt-1">Set the week's challenge per category</h3>
          <p className="text-sm text-stone-600 mt-1 max-w-xl">People who share in a category see one challenge a week. Tap the pencil to write your own or start from a ready-made idea — or leave the default (3 check-ins · +30 pts). Custom challenges are shared with everyone who takes part; only the coach who wrote one can edit or remove it.</p>
        </div>
        <div className="flex gap-1 rounded-full bg-stone-900/5 p-1" role="tablist">
          {[["this", `This week · ${data.this_week}`], ["next", `Next week · ${data.next_week}`]].map(([k, label]) => (
            <button key={k} role="tab" aria-selected={which === k} onClick={() => setWhich(k)} data-testid={`challenge-week-${k}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${which === k ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900"}`}>{label}</button>
          ))}
        </div>
      </div>
      <ul className="grid gap-2 mt-4 sm:grid-cols-2">
        {rows.map((ch) => <ChallengeRow key={`${ch.category}-${ch.which}`} ch={ch} templates={templates[ch.category] || []} onChanged={load} />)}
      </ul>
    </section>
  );
}
